import { apiClient } from '@/api/client';
import type { PaginatedResult } from '@/types/api';
import type {
  Domain, DnsRecord, DomainListParams, DnsZone, Onboarding, ZoneRecord, ZoneRecordPayload,
  DkimRotateResult, DnsValidationResult, DnsHealthResult, VerifyDomainResult, SyncReport, WebmailStatus,
} from '@/features/domains/types';
import type { DomainFormValues } from '@/features/domains/schema';

const enc = encodeURIComponent;

/** Domain management — mail-admin-service /domains endpoints. Ids URL-encoded. */
export const domainsApi = {
  list: async (params: DomainListParams): Promise<PaginatedResult<Domain>> =>
    (await apiClient.get<PaginatedResult<Domain>>('/domains', { params })).data,
  get: async (id: string): Promise<Domain> => (await apiClient.get<Domain>(`/domains/${enc(id)}`)).data,
  create: async (body: DomainFormValues): Promise<Domain> => (await apiClient.post<Domain>('/domains', body)).data,
  update: async (id: string, body: Partial<{ status: string; catchAllEnabled: boolean; catchAllTarget: string; dnsMode: string }>): Promise<Domain> =>
    (await apiClient.patch<Domain>(`/domains/${enc(id)}`, body)).data,
  remove: async (id: string): Promise<void> => { await apiClient.delete(`/domains/${enc(id)}`); },
  dnsRecords: async (id: string): Promise<DnsRecord[]> => {
    const { data } = await apiClient.get<DnsRecord[] | { records: DnsRecord[] }>(`/domains/${enc(id)}/dns-records`);
    return Array.isArray(data) ? data : data.records;
  },
  onboarding: async (id: string): Promise<Onboarding> =>
    (await apiClient.get<Onboarding>(`/domains/${enc(id)}/onboarding`, { timeout: 30_000 })).data,
  verify: async (id: string): Promise<VerifyDomainResult> =>
    (await apiClient.post<VerifyDomainResult>(`/domains/${enc(id)}/verify`, {})).data,

  /* managed-DNS zone editing (4.12) */
  zones: async (): Promise<DnsZone[]> => (await apiClient.get<DnsZone[]>('/dns/zones')).data,
  createZone: async (domainId: string): Promise<DnsZone> =>
    (await apiClient.post<DnsZone>('/dns/zones', { domainId })).data,
  removeZone: async (zoneId: string): Promise<void> => { await apiClient.delete(`/dns/zones/${enc(zoneId)}`); },
  zoneRecords: async (zoneId: string): Promise<ZoneRecord[]> =>
    (await apiClient.get<ZoneRecord[]>(`/dns/zones/${enc(zoneId)}/records`)).data,
  createRecord: async (zoneId: string, body: ZoneRecordPayload): Promise<ZoneRecord> =>
    (await apiClient.post<ZoneRecord>(`/dns/zones/${enc(zoneId)}/records`, body)).data,
  updateRecord: async (zoneId: string, recordId: string, body: ZoneRecordPayload): Promise<ZoneRecord> =>
    (await apiClient.put<ZoneRecord>(`/dns/zones/${enc(zoneId)}/records/${enc(recordId)}`, body)).data,
  deleteRecord: async (zoneId: string, recordId: string): Promise<void> => { await apiClient.delete(`/dns/zones/${enc(zoneId)}/records/${enc(recordId)}`); },
  validateZone: async (zoneId: string): Promise<DnsValidationResult> =>
    (await apiClient.post<DnsValidationResult>(`/dns/zones/${enc(zoneId)}/validate`, {})).data,
  publishZone: async (zoneId: string): Promise<DnsZone> =>
    (await apiClient.post<DnsZone>(`/dns/zones/${enc(zoneId)}/publish`, {})).data,
  zoneHealth: async (zoneId: string): Promise<DnsHealthResult> =>
    (await apiClient.get<DnsHealthResult>(`/dns/zones/${enc(zoneId)}/health`)).data,
  /* sync: add the domain's verified + current-host records (draft; never edits or deletes) */
  previewZoneSync: async (zoneId: string): Promise<SyncReport> =>
    (await apiClient.get<SyncReport>(`/dns/zones/${enc(zoneId)}/sync`, { timeout: 60_000 })).data,
  applyZoneSync: async (zoneId: string): Promise<SyncReport> =>
    (await apiClient.post<SyncReport>(`/dns/zones/${enc(zoneId)}/sync`, {}, { timeout: 60_000 })).data,
  webmailStatus: async (zoneId: string): Promise<WebmailStatus> =>
    (await apiClient.get<WebmailStatus>(`/dns/zones/${enc(zoneId)}/webmail`, { timeout: 30_000 })).data,
  rotateDkim: async (id: string): Promise<DkimRotateResult> =>
    (await apiClient.post<DkimRotateResult>(`/domains/${enc(id)}/dkim/rotate`, {})).data,
  setMailState: async (id: string, action: 'suspend' | 'resume'): Promise<Domain> =>
    (await apiClient.post<Domain>(`/domains/${enc(id)}/${action}`, {})).data,
};
